/**
 * Caption font catalog — the single source of truth for which fonts the editor
 * offers, how the dropdown groups/labels them, and which webfonts to load in the
 * browser preview.
 *
 * ⚠️ Export parity: the ffmpeg renderer burns captions with libass, which needs
 * the SAME font files installed in its container. When you add/remove a Google
 * font here, mirror it in `apps/ecs/shorts-renderer-image/fonts.txt` (the
 * renderer downloads those TTFs at image-build time). `family` MUST match the
 * font's real family name — it is written verbatim into both the canvas
 * `fontFamily` and the ASS `Fontname`.
 */

export interface FontDef {
  /** Real family name — used for canvas fontFamily AND ASS Fontname. */
  family: string;
  /** Human label shown in the dropdown. */
  label: string;
  group: 'ko' | 'latin' | 'system';
  /** Present → loaded from Google Fonts. `weights` lists the faces to request
   *  (only weights that actually exist for the family; requesting a missing one
   *  makes the CSS2 API 400). Omit for single-weight (400-only) families. */
  google?: { weights?: number[] };
}

/**
 * Curated "CapCut-style" set: punchy Korean display faces + Latin impact/fun
 * faces for English captions. Pretendard (the app default) is loaded separately
 * via index.css; system fonts need no loading.
 */
export const CAPTION_FONTS: FontDef[] = [
  // ── Korean (한글) ───────────────────────────────────────────
  { family: 'Pretendard', label: '프리텐다드 (기본)', group: 'ko' },
  { family: 'Black Han Sans', label: '검은고딕 (임팩트)', group: 'ko', google: {} },
  { family: 'Do Hyeon', label: '도현 (굵은 자막)', group: 'ko', google: {} },
  { family: 'Jua', label: '주아 (동글동글)', group: 'ko', google: {} },
  { family: 'Gaegu', label: '개구 (손글씨)', group: 'ko', google: { weights: [400, 700] } },
  { family: 'Gamja Flower', label: '감자꽃 (귀여운 손글씨)', group: 'ko', google: {} },
  { family: 'Dongle', label: '동글 (둥근)', group: 'ko', google: { weights: [400, 700] } },
  { family: 'Gugi', label: '구기 (개성 장식)', group: 'ko', google: {} },
  { family: 'Poor Story', label: '푸어스토리 (만화체)', group: 'ko', google: {} },
  { family: 'Hi Melody', label: '하이멜로디 (귀여움)', group: 'ko', google: {} },
  { family: 'Yeon Sung', label: '연성 (붓글씨)', group: 'ko', google: {} },
  { family: 'Kirang Haerang', label: '기랑해랑 (복고)', group: 'ko', google: {} },
  { family: 'Song Myung', label: '송명 (명조 감성)', group: 'ko', google: {} },
  { family: 'Gowun Dodum', label: '고운돋움 (깔끔)', group: 'ko', google: {} },
  // ── Latin (영문) ────────────────────────────────────────────
  { family: 'Anton', label: 'Anton (임팩트)', group: 'latin', google: {} },
  { family: 'Bebas Neue', label: 'Bebas Neue (콘덴스드)', group: 'latin', google: {} },
  { family: 'Bangers', label: 'Bangers (코믹)', group: 'latin', google: {} },
  { family: 'Luckiest Guy', label: 'Luckiest Guy (카툰)', group: 'latin', google: {} },
  {
    family: 'Fredoka',
    label: 'Fredoka (둥근 볼드)',
    group: 'latin',
    google: { weights: [400, 600] },
  },
  { family: 'Righteous', label: 'Righteous (레트로)', group: 'latin', google: {} },
  { family: 'Pacifico', label: 'Pacifico (스크립트)', group: 'latin', google: {} },
  { family: 'Permanent Marker', label: 'Permanent Marker (마커)', group: 'latin', google: {} },
  { family: 'Titan One', label: 'Titan One (둥근 볼드)', group: 'latin', google: {} },
  { family: 'Archivo Black', label: 'Archivo Black (강한 고딕)', group: 'latin', google: {} },
  // ── System ──────────────────────────────────────────────────
  { family: 'Arial', label: 'Arial', group: 'system' },
  { family: 'sans-serif', label: 'sans-serif (시스템)', group: 'system' },
];

/** Families we can wait on via `document.fonts` before painting (excludes the
 *  generic/system entries, which have no loadable face). */
export const MANAGED_FONT_FAMILIES: ReadonlySet<string> = new Set(
  CAPTION_FONTS.filter((f) => f.group !== 'system').map((f) => f.family)
);

/** Build the Google Fonts CSS2 URL covering every `google` family in the catalog. */
export function googleFontsHref(): string {
  const families = CAPTION_FONTS.filter((f) => f.google).map((f) => {
    const name = f.family.replace(/ /g, '+');
    const w = f.google?.weights;
    return w && w.length ? `family=${name}:wght@${w.join(';')}` : `family=${name}`;
  });
  return `https://fonts.googleapis.com/css2?${families.join('&')}&display=swap`;
}

/** Inject the Google Fonts stylesheet once (idempotent). Call at app startup. */
export function injectCaptionFonts(): void {
  if (typeof document === 'undefined' || document.getElementById('caption-fonts')) return;
  for (const [rel, href] of [
    ['preconnect', 'https://fonts.googleapis.com'],
    ['preconnect', 'https://fonts.gstatic.com'],
  ] as const) {
    const l = document.createElement('link');
    l.rel = rel;
    l.href = href;
    if (rel === 'preconnect') l.crossOrigin = '';
    document.head.appendChild(l);
  }
  const link = document.createElement('link');
  link.id = 'caption-fonts';
  link.rel = 'stylesheet';
  link.href = googleFontsHref();
  document.head.appendChild(link);
}
